import type {
  IMLSNumbers,
  IGenericUser,
  IBranchAdminUser,
  ITransferUser,
  IFrontEndInstructions,
  AltPhoneTypes,
  UserLicenseType,
  IOfferSettings,
} from '@bb/users';
import { UserRoles } from '@bb/users';
import type { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import type {
  Pronoun,
  UserFeatures,
  UserSubRoles,
} from '@bb/users/dist/types/enums';
import type { SupraSystemSubscriptionType } from '@bb/domain/dist/types/SupraSystemSubscription';
import type SupraSystemSubscription from '@bb/domain/dist/types/SupraSystemSubscription';
import type {
  CurrentUser,
  CurrentUserOrgDTO,
  IUserGetUserDefaultContactsResponse,
  JsonObject,
} from '@/app-package';

export type MemberNumbersState = Partial<
  // Keeping dirty state here makes it easier to track wrt array manipulations
  Omit<IMLSNumbers, 'board'> & {
    isPrimary?: boolean;
    boardDirty?: boolean;
    numberDirty?: boolean;
    mlsUniqueIdDirty?: boolean;
    board: IMLSNumbers['board'] | 'NO_BOARD';
    boardId: string | null;
  }
>[];

export type UserDefaultContact =
  IUserGetUserDefaultContactsResponse['contacts'][0] & {
    avatarFilestackId?: string;
    firstName?: string;
    lastName?: string;
    location?: string;
  };

export const tabKey = [
  'PROFILE',
  'SUPRA_SUBSCRIPTION',
  'FRONT_DESK_INSTRUCTIONS',
  'LISTING_SETTINGS',
  'LIVE_CONCIERGE_SERVICE',
  'NOTIFICATION_PREFERENCES',
  'BILLING',
  'PERMISSIONS',
] as const;
export type TabKey = (typeof tabKey)[number];

export interface UserViewPageProps {
  isNew?: boolean;
  isProfile?: boolean;
  isIncomingTransfer?: boolean;
}

export type UserViewPageParams = {
  userId?: string;
  showHistory?: string;
  transferId?: string;
};

export type UserPageSupraNotificationToggles = Pick<
  NonNullable<SupraSystemSubscription['notificationSettings']>,
  'showingStarted' | 'lockboxAssignmentReminder'
>;

export interface UserState {
  avatarFilestackId?: string;
  avatarFullFilestackId?: string;
  firstName?: string;
  lastName?: string;
  role: UserRoles;
  subRole?: UserSubRoles | '';
  title?: string;
  licenseType?: UserLicenseType;
  memberNumbers: MemberNumbersState;
  mobile?: string;
  officeExtension?: string;
  altPhone?: string;
  altPhoneType?: AltPhoneTypes;
  email?: string;
  branch?: string;
  team: string | undefined;
  address?: string;
  branchAccess?: string[];
  teamAccess?: string[];
  password?: string;
  disabled?: boolean;
  isAgentChatHidden?: boolean;
  isChatAdmin?: boolean;
  isChatInactive?: boolean;
  isDeleted?: boolean;
  offerSettings?: IOfferSettings;
  pronoun?: Pronoun[];
  frontEndInstructions?: IFrontEndInstructions;
  duplicateNotify?: boolean;
  expiryReminder?: boolean;
  feedbackCollected?: boolean;
  feedbackNotify?: boolean;
  feedbackSendToSeller?: boolean;
  billingCustomerNumber: string;
  features?: UserFeatures[];
  supraSubscription?: {
    type?: SupraSystemSubscriptionType | '';
    billing?: 'billable' | 'non-billable' | '';
    assignLockboxesReleaseShackles?: boolean;
    waiveActivationFee?: boolean;
    waiveActivationFeeReason?: string;
    singleAccessEnabled?: boolean;
    [key: string]: any;
  };
  id?: string;
  org?: string | CurrentUserOrgDTO;
  notes?: string;
  usingDataConnectAPI?: boolean;
}

export interface ForwardRepDirty {
  id: boolean;
  forwardRepsStart: boolean;
  forwardRepsExpiry: boolean;
}

export interface UserStateDirty {
  firstName: boolean;
  lastName: boolean;
  mobile: boolean;
  altPhone: boolean;
  email: boolean;
  branch: boolean;
  team: boolean;
  address: boolean;
  password: boolean;
  frontEndInstructions: {
    forwardCalls: { enabled: boolean; name: boolean; phone: boolean };
    vacationDates: { enabled: boolean; from: boolean; to: boolean };
    forwardReps: ForwardRepDirty[];
    releaseCell: boolean;
    notes: boolean;
    copierCode: boolean;
  };
  licenseType: boolean;
  subRole: boolean;
  features: boolean;
  supraSubscription: {
    type: boolean;
    billing: boolean;
    assignLockboxesReleaseShackles: boolean;
    waiveActivationFeeReason: boolean;
  };
}

export type ValidationKeys =
  | 'firstNameValid'
  | 'lastNameValid'
  | 'mobileValid'
  | 'altPhoneTypeValid'
  | 'altPhoneNumberValid'
  | 'emailValid'
  | 'passwordValid'
  | 'frontDeskValid'
  | 'branchValid'
  | 'addressValid'
  | 'branchAccessValid'
  | 'teamAccessValid'
  | 'memberNumbersAllValid'
  | 'licenseTypeValid'
  | 'supraSubscriptionValid'
  | 'userSubRoleValid'
  | 'waiveActivationFeeReasonValid'
  | 'supraSubscriptionTypeValid'
  | 'supraSubscriptionBillingValid';

export type ValidationValue = {
  field: boolean;
  dirtyKey: Exclude<
    keyof UserStateDirty,
    'frontEndInstructions' | 'supraSubscription'
  >;
  getDirtyState?: () => Partial<UserStateDirty>;
  getDirtyUserStateField?: () => Partial<UserState>;
  tabKey: TabKey;
};

export type AlertType =
  | 'unverified'
  | 'incomingTransfer'
  | 'outgoingTransfer'
  | 'suggestTransfer'
  | 'noAccess'
  | undefined;

export const isUserAuthenticated = (
  userResponse: any,
): userResponse is JsonObject<IGenericUser> =>
  userResponse && 'notAuthenticated' in userResponse
    ? !userResponse.notAuthenticated
    : true;

// export const isCurrentUser = (
//   userResponse: { org: unknown },
//   // | IGenericUser
//   // | CurrentUser
//   // | BuildJsonType<ITransferUser>,
// ): userResponse is CurrentUser => typeof userResponse.org !== 'string';

export const isCurrentUser = (input: any): input is CurrentUser => true;

type CurrentBranchAdmin = Omit<
  IBranchAdminUser,
  'org' | 'password' | 'mls_numbers'
> & {
  assumingUserId?: string;
  isAssumedNotBB?: boolean;
  org: CurrentUser['org'];
  mls_numbers: CurrentUser['mls_numbers'];
};

export const isCurrentUserBranchAdmin = (
  currentUser: any,
): currentUser is CurrentBranchAdmin =>
  // return currentUser.role === UserRoles['Branch Admin'];
  true;

export const isUserBranchAdmin = (
  user: BuildJsonType<IGenericUser> | BuildJsonType<ITransferUser>,
): user is BuildJsonType<IBranchAdminUser> =>
  user.role === UserRoles['Branch Admin'];

export const isOfTypeTabKey = (keyInput: any): keyInput is TabKey =>
  tabKey.includes(keyInput);
